import { Form, Input, InputNumber, Switch, DatePicker, Select, notification } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { TCustomer, TSmartVariableDefinition } from '@/types/TSmartCRM';
import { EFieldType } from 'src/enums/ESmartCRM';
import { createPhoneValidator } from '@/utils/phoneValidation';
import { getFieldRules, getFieldPlaceholder } from '@/utils/SmartCRM/fieldUtils';
import moment from 'moment';

interface CustomerFormProps {
	onSave: (customerData: Omit<TCustomer, 'id'>) => void;
	onCancel: () => void;
	onBack?: () => void;
	initialData?: TCustomer | null;
	agentId?: string;
	variables?: TSmartVariableDefinition[];
	isEditing: boolean;
	loading: boolean;
}

interface CustomerFormData {
	name: string;
	email: string;
	number: string;
	[key: string]: any;
}

export const CustomerForm = ({
	onSave,
	onCancel,
	onBack,
	initialData,
	agentId,
	variables = [],
	isEditing,
	loading,
}: CustomerFormProps) => {
	const [form] = Form.useForm<CustomerFormData>();
	const [deletedVariables, setDeletedVariables] = useState<Set<string>>(new Set());
	const [selectedOptionalFields, setSelectedOptionalFields] = useState<Set<string>>(new Set());

	const getRequiredVariables = () => {
		return variables.filter(
			(variable) => variable.isRequired && !deletedVariables.has(variable.id),
		);
	};

	const getOptionalVariables = () => {
		return variables.filter((variable) => !variable.isRequired);
	};

	const getVisibleOptionalVariables = () => {
		if (isEditing && initialData?.variables && Array.isArray(initialData.variables)) {
			return getOptionalVariables().filter((variable) => {
				const variableData = initialData.variables.find((v) => v.variableId === variable.id);
				const hasValue =
					variableData &&
					variableData.value !== undefined &&
					variableData.value !== null &&
					variableData.value !== '';

				return (
					(hasValue || selectedOptionalFields.has(variable.id)) &&
					!deletedVariables.has(variable.id)
				);
			});
		} else {
			return getOptionalVariables().filter(
				(variable) => selectedOptionalFields.has(variable.id) && !deletedVariables.has(variable.id),
			);
		}
	};

	const getAvailableOptionalFields = () => {
		const visibleOptional = getVisibleOptionalVariables();
		const visibleIds = new Set(visibleOptional.map((v) => v.id));

		return getOptionalVariables().filter(
			(variable) => !visibleIds.has(variable.id) && !deletedVariables.has(variable.id),
		);
	};

	useEffect(() => {
		setDeletedVariables(new Set());
		setSelectedOptionalFields(new Set());

		if (isEditing && initialData) {
			const formData: CustomerFormData = {
				name: initialData.name || '',
				email: initialData.email || '',
				number: initialData.number || '',
			};

			const fieldsWithValues = new Set<string>();

			if (
				initialData.variables &&
				Array.isArray(initialData.variables) &&
				initialData.variables.length > 0
			) {
				variables.forEach((variable) => {
					const variableData = initialData.variables.find((v) => v.variableId === variable.id);

					if (
						variableData &&
						variableData.value !== undefined &&
						variableData.value !== null &&
						variableData.value !== ''
					) {
						if (!variable.isRequired) {
							fieldsWithValues.add(variable.id);
						}

						if (variable.fieldType === EFieldType.DATE && variableData.value) {
							formData[variable.internalKey] = moment(variableData.value);
						} else if (variable.fieldType === EFieldType.BOOLEAN) {
							formData[variable.internalKey] = Boolean(variableData.value);
						} else {
							formData[variable.internalKey] = variableData.value;
						}
					}
				});
			}

			setSelectedOptionalFields(fieldsWithValues);
			form.setFieldsValue(formData);
		} else {
			form.resetFields();
		}
	}, [isEditing, initialData, form, variables]);

	const phoneValidator = createPhoneValidator(form, 'number');

	const handleDeleteVariable = (variableId: string) => {
		const variable = variables.find((v) => v.id === variableId);
		if (variable) {
			form.setFieldsValue({ [variable.internalKey]: undefined });
			setDeletedVariables((prev) => new Set([...prev, variableId]));
			if (!variable.isRequired) {
				setSelectedOptionalFields((prev) => {
					const newSet = new Set(prev);
					newSet.delete(variableId);
					return newSet;
				});
			}
		}
	};

	const handleAddOptionalField = (variableId: string) => {
		setSelectedOptionalFields((prev) => new Set([...prev, variableId]));
	};

	const renderFieldComponent = (variable: TSmartVariableDefinition) => {
		const placeholder = getFieldPlaceholder(variable);

		switch (variable.fieldType) {
			case EFieldType.TEXT:
				return <Input placeholder={placeholder} disabled={loading} />;

			case EFieldType.NUMBER:
				return (
					<InputNumber style={{ width: '100%' }} placeholder={placeholder} disabled={loading} />
				);

			case EFieldType.BOOLEAN:
				return <Switch disabled={loading} />;

			case EFieldType.DATE:
				return (
					<DatePicker style={{ width: '100%' }} placeholder={placeholder} disabled={loading} />
				);

			case EFieldType.SELECT:
				return (
					<Select placeholder={placeholder} disabled={loading} style={{ width: '100%' }} allowClear>
						{variable.options?.map((option) => (
							<Select.Option key={option} value={option}>
								{option}
							</Select.Option>
						))}
					</Select>
				);

			case EFieldType.MULTISELECT:
				return (
					<Select
						mode="multiple"
						placeholder={placeholder}
						disabled={loading}
						style={{ width: '100%' }}
						allowClear
					>
						{variable.options?.map((option) => (
							<Select.Option key={option} value={option}>
								{option}
							</Select.Option>
						))}
					</Select>
				);

			default:
				return <Input placeholder={placeholder} disabled={loading} />;
		}
	};

	const handleSubmit = async (): Promise<void> => {
		try {
			const values = await form.validateFields();
			const { name, email, number, ...dynamicFields } = values;

			const variablesData: { [key: string]: any } = {};

			variables.forEach((variable) => {
				if (isEditing && deletedVariables.has(variable.id)) {
					return;
				}

				const fieldValue = dynamicFields[variable.internalKey];

				if (
					fieldValue !== undefined &&
					fieldValue !== null &&
					fieldValue !== '' &&
					!(Array.isArray(fieldValue) && fieldValue.length === 0)
				) {
					let processedValue = fieldValue;

					if (variable.fieldType === EFieldType.DATE && fieldValue) {
						processedValue = fieldValue.toDate ? fieldValue.toDate() : new Date(fieldValue);
					}

					variablesData[variable.id] = processedValue;
				}
			});

			const customerData: any = {
				number,
				agentId,
				variables: variablesData,
			};

			if (name && name.trim() !== '') {
				customerData.name = name;
			}

			if (email && email.trim() !== '') {
				customerData.email = email;
			}

			onSave(customerData);
		} catch (_error) {
			notification.error({
				message: 'Validation Error',
				description: 'Please fill in all required fields correctly.',
			});
		}
	};

	const renderVariableField = (variable: TSmartVariableDefinition, showDelete: boolean = true) => {
		return (
			<div key={variable.id} className="mb-4">
				<div className="flex items-center justify-between mb-2">
					<label className="block text-sm font-medium text-gray-700">
						{variable.fieldName}
						{variable.isRequired && <span className="text-red-500 ml-1">*</span>}
					</label>
					{showDelete && (
						<button
							type="button"
							className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded"
							onClick={() => handleDeleteVariable(variable.id)}
							title="Remove this field"
						>
							<DeleteOutlined />
						</button>
					)}
				</div>
				<Form.Item
					name={variable.internalKey}
					rules={
						variable.isRequired && !deletedVariables.has(variable.id)
							? getFieldRules(variable)
							: undefined
					}
					className="mb-0"
				>
					{renderFieldComponent(variable)}
				</Form.Item>
			</div>
		);
	};

	const requiredVariables = getRequiredVariables();
	const visibleOptionalVariables = getVisibleOptionalVariables();
	const availableOptionalFields = getAvailableOptionalFields();

	return (
		<div className="h-full flex flex-col">
			<div className="flex items-center justify-between mb-6 px-6 pt-6">
				{!isEditing && onBack && (
					<button type="button" className="text-blue-600 hover:text-blue-800" onClick={onBack}>
						← Back to options
					</button>
				)}
				<h3 className="text-lg font-semibold flex-1 text-center">
					{isEditing ? 'Edit Customer' : 'Add New Customer'}
				</h3>
				<div className="w-24" />
			</div>

			<div
				className="flex-1 px-6 overflow-y-auto"
				style={{
					maxHeight: 'calc(80vh - 160px)',
					minHeight: '400px',
				}}
			>
				<Form form={form} layout="vertical">
					<Form.Item name="name" label="Full Name">
						<Input placeholder="Enter customer name" disabled={loading} />
					</Form.Item>

					<Form.Item name="email" label="Email Address">
						<Input placeholder="Enter email address" disabled={loading} />
					</Form.Item>

					<Form.Item
						name="number"
						label="Phone Number"
						rules={[
							{ required: true, message: 'Please enter phone number' },
							{ validator: phoneValidator },
						]}
					>
						<Input placeholder="Enter phone number" disabled={loading || isEditing} />
					</Form.Item>

					{requiredVariables.length > 0 && (
						<div className="mb-6">
							<h4 className="text-md font-medium mb-4 text-gray-700 border-b pb-2">
								Required Variables
							</h4>
							{requiredVariables.map((variable) => renderVariableField(variable, isEditing))}
						</div>
					)}

					<div className="mb-6">
						<div className="flex items-center justify-between mb-4">
							<h4 className="text-md font-medium text-gray-700 border-b pb-2 flex-1">
								Optional Variables
								{visibleOptionalVariables.length > 0 && (
									<span className="text-sm font-normal text-gray-500 ml-1">
										({visibleOptionalVariables.length} selected)
									</span>
								)}
							</h4>
						</div>

						{availableOptionalFields.length > 0 && (
							<div className="mb-4">
								<Select
									placeholder="Select variable to add"
									style={{ width: '100%' }}
									onChange={handleAddOptionalField}
									value={undefined}
									disabled={loading}
								>
									{availableOptionalFields.map((variable) => (
										<Select.Option key={variable.id} value={variable.id}>
											{variable.fieldName}
										</Select.Option>
									))}
								</Select>
							</div>
						)}

						{visibleOptionalVariables.map((variable) => renderVariableField(variable, true))}
					</div>
				</Form>
			</div>

			<div className="border-t bg-white px-6 py-4">
				<div className="flex justify-end space-x-3">
					<button
						type="button"
						className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
						onClick={onCancel}
						disabled={loading}
					>
						Cancel
					</button>
					<button
						type="button"
						className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
						onClick={handleSubmit}
						disabled={loading}
					>
						{loading ? 'Saving...' : isEditing ? 'Update Customer' : 'Add Customer'}
					</button>
				</div>
			</div>
		</div>
	);
};
